import Phaser from 'phaser';
import { getCurrentTheme } from '../Utils.js';
import { showMarqueeMessage } from '../Utils.js';
import { MarqueeManager } from '../ui/MarqueeManager.js';

export class InstructionsScene extends Phaser.Scene {
  constructor() {
    super({ key: 'InstructionsScene' });
  }

  preload() {
    // Load theme background image if available
    const theme = getCurrentTheme();
    if (theme && theme.backgroundImage) {
      const backgroundImageUrl = `https://soulwars.s3.us-east-1.amazonaws.com/themes/${theme.id}/${theme.backgroundImage}`;
      this.load.image('themeBackground', backgroundImageUrl);

      // Handle load errors gracefully
      this.load.on('filecomplete', (key) => {
        if (key === 'themeBackground') {
          console.log('✅ Theme background loaded successfully');
        }
      });

      this.load.on('loaderror', (file) => {
        if (file.key === 'themeBackground') {
          console.warn('⚠️ Failed to load theme background, using fallback');
        }
      });
    }
  }

  create() {
    const { width, height } = this.cameras.main;
    
    // Create background with gradient effect
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x1F2937);

    //add the theme background image
    const themeBackground = this.add.image(width / 2, height / 2, 'themeBackground');
    themeBackground.setAlpha(0.5);
    //this.container.add(themeBackground);
    
    // Create main container
    this.container = this.add.container(width / 2, height / 2);
    
    // Main panel background - make it larger to accommodate 2 rows of cards
    const panelBg = this.add.rectangle(0, 50, 700, 380, 0x000000);
    panelBg.setStrokeStyle(2, 0x4B5563);
    panelBg.setAlpha(0.8);
    this.container.add(panelBg);
    
    const titleStartY = -50
        
    // How to Play section - move up
    const howToPlayTitle = this.add.text(0, titleStartY, 'Ready to Battle?', {
      fontSize: '28px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.container.add(howToPlayTitle);
    
    const instructions = [
      '1. Connect Chat (Optional) - Let your audience interact with the game, using pumpfun or restream (YouTube, TikTok, X, Twitch, Kick, etc.)',
      '2. Start the matches - Click "Launch" to begin the battle!'
    ];
    
    instructions.forEach((instruction, index) => {
      const text = this.add.text(0, titleStartY + 60 + (index * 65), instruction, {
        fontSize: '24px',
        color: '#D1D5DB',
        fontFamily: 'Arial, sans-serif',
        wordWrap: { width: 600 }
      }).setOrigin(0.5).setWordWrapWidth(600);
      this.container.add(text);
    });
    
    
    // Start button - adjust position for new layout
    const startButton = this.add.rectangle(0, titleStartY + 220, 200, 50, 0x10B981);
    startButton.setStrokeStyle(2, 0x059669);
    this.container.add(startButton);
    
    const startText = this.add.text(0, titleStartY + 220, '🚀 Launch', {
      fontSize: '20px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.container.add(startText);
    
    // Check game access before making button interactive
    const hasGameAccess = window && window.__SW_HAS_GAME_ACCESS__;
    
    if (!hasGameAccess) {
      // Disable button visually
      startButton.setFillStyle(0x6B7280);
      startButton.setAlpha(0.5);
      startText.setText('🔒 Access Required');
      startText.setStyle({ fontSize: '18px' });
      
      // Show access required message
      const accessMsg = this.add.text(0, titleStartY + 580, 'Connect wallet and get game access to play', {
        fontSize: '14px',
        color: '#EF4444',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(0.5);
      this.container.add(accessMsg);
    } else {
      // Make button interactive only if user has access
      startButton.setInteractive({ useHandCursor: true });
      startButton.on('pointerdown', async () => {
        // Read latest config from game OR from React-injected window vars
        const gm = (this.game && this.game.config && this.game.config.gameMode) || (window && window.__SW_SELECTED_MODE__) || 'free-for-all';
        const tc = (this.game && this.game.config && this.game.config.teamCount) || (window && window.__SW_TEAM_COUNT__) || 2;
        console.log('[InstructionsScene] Launch clicked, creating game session...');
        
        // Create game session before navigating
        try {
          const token = localStorage.getItem('jwt_token');
          if (token) {
            const response = await fetch('/api/sessions/create', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
              },
              body: JSON.stringify({
                metadata: {
                  gameMode: gm,
                  teamCount: tc
                }
              })
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.sessionId) {
                const sessionId = data.sessionId;
                console.log('[InstructionsScene] Game session created:', sessionId);
                
                // Store sessionId in BattleStateManager and window for access across scenes
                try {
                  const BattleStateManager = (await import('../game/BattleStateManager.js')).default;
                  BattleStateManager.setSessionId(sessionId);
                } catch (e) {
                  console.warn('[InstructionsScene] Failed to set sessionId in BattleStateManager:', e);
                }
                
                try {
                  window.__SW_SESSION_ID__ = sessionId;
                  window.__SW_SESSION_ROOM_ID__ = sessionId; // Also set as room ID for chat
                } catch (e) {
                  console.warn('[InstructionsScene] Failed to set sessionId in window:', e);
                }
                
                // Trigger event to notify Game.jsx to connect to chat room
                try {
                  window.dispatchEvent(new CustomEvent('soulwars-session-created', { detail: { sessionId } }));
                } catch (e) {
                  console.warn('[InstructionsScene] Failed to dispatch session-created event:', e);
                }
              }
            } else {
              console.warn('[InstructionsScene] Failed to create session, proceeding anyway');
            }
          } else {
            console.warn('[InstructionsScene] No JWT token, proceeding without session');
          }
        } catch (error) {
          console.error('[InstructionsScene] Error creating session:', error);
          // Continue anyway - session creation is not critical for game start
        }
        
        console.log('[InstructionsScene] Navigating to JoinScene with config', { gameMode: gm, teamCount: tc });
        // Navigate to JoinScene instead of directly to PreloadScene
        this.scene.start('JoinScene', { gameMode: gm, teamCount: tc });
      });
      
      startButton.on('pointerover', () => {
        startButton.setFillStyle(0x059669);
        startButton.setScale(1.05);
      });
      
      startButton.on('pointerout', () => {
        startButton.setFillStyle(0x10B981);
        startButton.setScale(1);
      });
    }
    
    
    this.tweens.add({
      targets: this.container,
      alpha: { from: 0, to: 1 },
      scaleX: { from: 0.8, to: 1 },
      scaleY: { from: 0.8, to: 1 },
      duration: 800,
      ease: 'Back.easeOut'
    });

    // Initialize global marquee manager if not already done
    if (!this.game.marqueeManager) {
      this.game.marqueeManager = new MarqueeManager(this);
      this.game.marqueeManager.init();
      console.log('[InstructionsScene] Global marquee manager initialized');
    }

    // Start marquee test messages
    this.startMarqueeTest();

    // Listen for resize events
    this.scale.on('resize', this.handleResize, this);
  }

  handleResize(gameSize) {
    const { width, height } = gameSize;

    // Update background size and position
    if (this.background && this.background.active) {
      try {
        this.background.setPosition(width / 2, height / 2);
        this.background.setSize(width, height);
      } catch (e) {
        console.warn('Failed to resize background:', e);
      }
    }

    // Update container position to stay centered
    if (this.container && this.container.active) {
      try {
        this.container.setPosition(width / 2, height / 2);
      } catch (e) {
        console.warn('Failed to reposition container:', e);
      }
    }
  }

  startMarqueeTest() {
    // Add a small delay to ensure marquee manager is fully initialized
    this.time.delayedCall(100, () => {
      // Show first message
      this.showRandomMarqueeMessage();

      // Set up interval to show messages every 3-5 seconds
      this.marqueeTestTimer = this.time.addEvent({
        delay: Phaser.Math.Between(3000, 5000), // 3-5 seconds
        callback: () => {
          this.showRandomMarqueeMessage();
          // Reset timer for next random interval
          this.marqueeTestTimer.reset({
            delay: Phaser.Math.Between(3000, 5000),
            callback: () => this.showRandomMarqueeMessage(),
            loop: true
          });
        },
        loop: true
      });
    });
  }

  showRandomMarqueeMessage() {
    const testMessages = [
      { text: '🎮 Welcome to Soul Wars!', color: '#10B981' },
      { text: '⚔️ Battle starting soon...', color: '#EF4444' },
      { text: '👑 Choose your hero wisely!', color: '#F59E0B' },
      { text: '💎 Special abilities unlocked!', color: '#8B5CF6' },
      { text: '🏆 Victory awaits the brave!', color: '#F97316' },
      { text: '🔥 Epic battles incoming!', color: '#DC2626' },
      { text: '⚡ Power up your warriors!', color: '#EAB308' },
      { text: '🌟 Legendary moments ahead!', color: '#A855F7' },
      { text: '🛡️ Defend your territory!', color: '#06B6D4' },
      { text: '🎯 Precision is key!', color: '#84CC16' }
    ];

    const randomMessage = testMessages[Math.floor(Math.random() * testMessages.length)];

    showMarqueeMessage(randomMessage.text, {
      color: randomMessage.color,
      fontSize: '16px'
    }, 'low');
  }

  shutdown() {
    // Clean up resize listener
    this.scale.off('resize', this.handleResize, this);

    // Clean up marquee test timer
    if (this.marqueeTestTimer) {
      this.marqueeTestTimer.destroy();
      this.marqueeTestTimer = null;
    }
  }
}
